import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  PlusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Alert, Badge, Button, Card } from '../../components/common/UIComponents';
import { PageEmptyState, PageLoadingState } from '../../components/common/ViewStates';
import { useSearchParams } from 'react-router-dom';
import { EquipmentType, EquipmentTypeSuggestion } from '../../types/firestore';
import {
  adminCreateEquipmentType,
  adminSetEquipmentTypeActiveState,
  adminUpdateEquipmentType,
  moderateEquipmentTypeSuggestion,
} from '../../services/equipmentTypeAdminService';
import { getEquipmentTypeCatalogForAdmin } from '../../services/equipmentTypeCatalogService';
import { getPendingEquipmentTypeSuggestionsForAdmin } from '../../services/equipmentTypeSuggestionService';
import { notificationService } from '../../services/notificationService';

type AdminView = 'catalog' | 'moderation';

type EquipmentTypeFormDraft = {
  name: string;
  shortCode: string;
};

type SuggestionActionDraft = {
  shortCode: string;
  rejectionReason: string;
};

const normalizeInput = (value: string): string => value.trim().replace(/\s+/g, ' ');

const formatTimestamp = (
  value?: EquipmentType['createdAt'] | EquipmentTypeSuggestion['createdAt']
) => {
  if (!value || typeof (value as { toDate?: () => Date }).toDate !== 'function') {
    return 'Brak daty';
  }
  return (value as { toDate: () => Date }).toDate().toLocaleString('pl-PL');
};

const getFriendlyError = (fallback: string, err: unknown): string => {
  const code = String((err as { code?: string })?.code || '').toLowerCase();
  if (code.includes('permission-denied')) {
    return 'Brak uprawnień administratora do tej operacji.';
  }
  if (code.includes('already-exists')) {
    return 'Typ sprzętu o tej nazwie lub shortCode już istnieje albo oczekuje na moderację.';
  }
  if (code.includes('not-found')) {
    return 'Nie znaleziono wskazanego rekordu.';
  }
  if (code.includes('failed-precondition')) {
    return 'Rekord nie jest w stanie, który pozwala na tę operację.';
  }
  return fallback;
};

const getStatusBadge = (status: EquipmentType['status']) =>
  status === 'active' ? (
    <Badge variant="success" size="sm">
      Aktywny
    </Badge>
  ) : (
    <Badge variant="warning" size="sm">
      Nieaktywny
    </Badge>
  );

const EquipmentTypeManagementPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const requestedSuggestionId = searchParams.get('equipmentTypeSuggestionId') || '';
  const requestedNotificationId = searchParams.get('notificationId') || '';
  const [view, setView] = useState<AdminView>('catalog');
  const [catalog, setCatalog] = useState<EquipmentType[]>([]);
  const [pendingSuggestions, setPendingSuggestions] = useState<EquipmentTypeSuggestion[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [creatingDraft, setCreatingDraft] = useState<EquipmentTypeFormDraft>({
    name: '',
    shortCode: '',
  });
  const [editingEquipmentTypeId, setEditingEquipmentTypeId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<EquipmentTypeFormDraft>({
    name: '',
    shortCode: '',
  });
  const [actionDrafts, setActionDrafts] = useState<Record<string, SuggestionActionDraft>>({});
  const [error, setError] = useState<string | null>(null);

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    setCatalogError(null);
    try {
      const nextCatalog = await getEquipmentTypeCatalogForAdmin();
      setCatalog(nextCatalog);
    } catch (err) {
      console.error('Failed to load equipment type catalog:', err);
      setCatalogError('Nie udało się załadować katalogu typów sprzętu.');
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  const loadSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    setSuggestionsError(null);
    try {
      const nextSuggestions = await getPendingEquipmentTypeSuggestionsForAdmin();
      setPendingSuggestions(nextSuggestions);
    } catch (err) {
      console.error('Failed to load equipment type suggestions:', err);
      setSuggestionsError('Nie udało się załadować kolejki moderacji typów sprzętu.');
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([loadCatalog(), loadSuggestions()]);
  }, [loadCatalog, loadSuggestions]);

  useEffect(() => {
    void refreshData();
  }, [refreshData]);

  useEffect(() => {
    if (requestedView === 'moderation' && requestedSuggestionId) {
      setView('moderation');
    }
  }, [requestedSuggestionId, requestedView]);

  useEffect(() => {
    if (
      requestedView !== 'moderation' ||
      view !== 'moderation' ||
      !requestedSuggestionId ||
      !requestedNotificationId ||
      loadingSuggestions ||
      suggestionsError
    ) {
      return;
    }

    const exactSuggestion = pendingSuggestions.find(
      (suggestion) => suggestion.equipmentTypeSuggestionId === requestedSuggestionId
    );
    if (!exactSuggestion) {
      return;
    }

    void notificationService
      .markEquipmentTypeSuggestionNotificationAsRead({
        notificationId: requestedNotificationId,
        suggestionId: exactSuggestion.equipmentTypeSuggestionId,
      })
      .catch((error) => {
        console.error('Failed to mark equipment type suggestion notification as read:', error);
      });
  }, [
    loadingSuggestions,
    pendingSuggestions,
    requestedNotificationId,
    requestedSuggestionId,
    requestedView,
    suggestionsError,
    view,
  ]);

  const filteredCatalog = useMemo(() => {
    const query = catalogSearch.trim().toLowerCase();
    if (!query) {
      return catalog;
    }

    return catalog.filter((item) =>
      [item.name, item.shortCode, item.status, item.source].join(' ').toLowerCase().includes(query)
    );
  }, [catalog, catalogSearch]);

  const catalogStats = useMemo(
    () => ({
      total: catalog.length,
      active: catalog.filter((item) => item.status === 'active').length,
      inactive: catalog.filter((item) => item.status === 'inactive').length,
    }),
    [catalog]
  );

  const suggestionStats = useMemo(
    () => ({
      total: pendingSuggestions.length,
      incomplete: pendingSuggestions.filter((item) => !item.suggestedName.trim()).length,
    }),
    [pendingSuggestions]
  );

  const startEdit = (item: EquipmentType) => {
    setEditingEquipmentTypeId(item.equipmentTypeId);
    setEditingDraft({
      name: item.name,
      shortCode: item.shortCode,
    });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingEquipmentTypeId(null);
    setEditingDraft({ name: '', shortCode: '' });
  };

  const handleCreateEquipmentType = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = normalizeInput(creatingDraft.name);
    const shortCode = normalizeInput(creatingDraft.shortCode);
    if (!name || !shortCode) {
      setError('Podaj nazwę i shortCode typu sprzętu.');
      return;
    }

    setBusyKey('catalog-create');
    setError(null);
    try {
      await adminCreateEquipmentType({ name, shortCode });
      setCreatingDraft({ name: '', shortCode: '' });
      await refreshData();
    } catch (err) {
      console.error('Failed to create equipment type:', err);
      setError(getFriendlyError('Nie udało się dodać typu sprzętu.', err));
    } finally {
      setBusyKey(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingEquipmentTypeId) {
      return;
    }

    const name = normalizeInput(editingDraft.name);
    const shortCode = normalizeInput(editingDraft.shortCode);
    if (!name || !shortCode) {
      setError('Nazwa i shortCode typu sprzętu nie mogą być puste.');
      return;
    }

    setBusyKey(`catalog-edit-${editingEquipmentTypeId}`);
    setError(null);
    try {
      await adminUpdateEquipmentType({
        equipmentTypeId: editingEquipmentTypeId,
        name,
        shortCode,
      });
      cancelEdit();
      await refreshData();
    } catch (err) {
      console.error('Failed to update equipment type:', err);
      setError(getFriendlyError('Nie udało się zaktualizować typu sprzętu.', err));
    } finally {
      setBusyKey(null);
    }
  };

  const handleToggleActiveState = async (item: EquipmentType) => {
    const action = item.status === 'active' ? 'deactivate' : 'reactivate';
    setBusyKey(`catalog-toggle-${item.equipmentTypeId}`);
    setError(null);
    try {
      await adminSetEquipmentTypeActiveState({
        equipmentTypeId: item.equipmentTypeId,
        action,
      });
      await refreshData();
    } catch (err) {
      console.error('Failed to toggle equipment type state:', err);
      setError(
        getFriendlyError(
          action === 'deactivate'
            ? 'Nie udało się wycofać typu sprzętu.'
            : 'Nie udało się przywrócić typu sprzętu.',
          err
        )
      );
    } finally {
      setBusyKey(null);
    }
  };

  const ensureSuggestionDraft = (suggestionId: string): SuggestionActionDraft =>
    actionDrafts[suggestionId] || { shortCode: '', rejectionReason: '' };

  const updateSuggestionDraft = (suggestionId: string, patch: Partial<SuggestionActionDraft>) => {
    setActionDrafts((current) => ({
      ...current,
      [suggestionId]: {
        ...ensureSuggestionDraft(suggestionId),
        ...patch,
      },
    }));
  };

  const handleApproveSuggestion = async (suggestion: EquipmentTypeSuggestion) => {
    const draft = ensureSuggestionDraft(suggestion.equipmentTypeSuggestionId);
    const shortCode = normalizeInput(draft.shortCode);
    if (!shortCode) {
      setError('Podaj shortCode przed zaakceptowaniem sugestii.');
      return;
    }

    setBusyKey(`suggestion-approve-${suggestion.equipmentTypeSuggestionId}`);
    setError(null);
    try {
      await moderateEquipmentTypeSuggestion({
        suggestionId: suggestion.equipmentTypeSuggestionId,
        action: 'approve',
        shortCode,
      });
      setActionDrafts((current) => {
        const next = { ...current };
        delete next[suggestion.equipmentTypeSuggestionId];
        return next;
      });
      await refreshData();
    } catch (err) {
      console.error('Failed to approve equipment type suggestion:', err);
      setError(getFriendlyError('Nie udało się zaakceptować sugestii typu sprzętu.', err));
    } finally {
      setBusyKey(null);
    }
  };

  const handleRejectSuggestion = async (suggestion: EquipmentTypeSuggestion) => {
    const draft = ensureSuggestionDraft(suggestion.equipmentTypeSuggestionId);
    setBusyKey(`suggestion-reject-${suggestion.equipmentTypeSuggestionId}`);
    setError(null);
    try {
      await moderateEquipmentTypeSuggestion({
        suggestionId: suggestion.equipmentTypeSuggestionId,
        action: 'reject',
        rejectionReason: normalizeInput(draft.rejectionReason),
      });
      setActionDrafts((current) => {
        const next = { ...current };
        delete next[suggestion.equipmentTypeSuggestionId];
        return next;
      });
      await refreshData();
    } catch (err) {
      console.error('Failed to reject equipment type suggestion:', err);
      setError(getFriendlyError('Nie udało się odrzucić sugestii typu sprzętu.', err));
    } finally {
      setBusyKey(null);
    }
  };

  if (
    loadingCatalog &&
    loadingSuggestions &&
    catalog.length === 0 &&
    pendingSuggestions.length === 0
  ) {
    return (
      <div className="space-y-6">
        <PageLoadingState
          title="Ładowanie katalogu typów sprzętu"
          message="Pobieramy canonical katalog oraz kolejkę moderacji."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold text-primary-light-100">
                Katalog typów sprzętu
              </h1>
              <Badge variant="info" size="sm">
                Canonical SoT
              </Badge>
            </div>
            <p className="max-w-3xl text-sm text-primary-light-300">
              Osobny katalog domenowy z moderacją admina i sugestiami serviceAdmin. shortCode jest
              stabilnym składnikiem przyszłego indeksu, ale nie licznikiem.
            </p>
          </div>

          <Button
            type="button"
            variant="neutral"
            size="sm"
            className="self-start"
            onClick={() => {
              void refreshData();
            }}
            icon={<ArrowPathIcon className="h-4 w-4" />}
          >
            Odśwież
          </Button>
        </div>
      </header>

      {(error || catalogError || suggestionsError) && (
        <div className="space-y-2">
          {error && <Alert variant="error">{error}</Alert>}
          {catalogError && <Alert variant="error">{catalogError}</Alert>}
          {suggestionsError && <Alert variant="error">{suggestionsError}</Alert>}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-3 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-[0.08em] text-primary-light-400">Wszystkie</p>
          <p className="mt-2 text-2xl font-semibold text-primary-light-100">{catalogStats.total}</p>
          <p className="mt-1 text-sm text-primary-light-300">rekordy canonical katalogu</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-[0.08em] text-primary-light-400">Aktywne</p>
          <p className="mt-2 text-2xl font-semibold text-primary-light-100">
            {catalogStats.active}
          </p>
          <p className="mt-1 text-sm text-primary-light-300">widoczne dla przyszłych consumerów</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-[0.08em] text-primary-light-400">
            Sugestie pending
          </p>
          <p className="mt-2 text-2xl font-semibold text-primary-light-100">
            {suggestionStats.total}
          </p>
          <p className="mt-1 text-sm text-primary-light-300">
            {suggestionStats.incomplete > 0
              ? `${suggestionStats.incomplete} rekordów wymaga ręcznej weryfikacji`
              : 'Brak niekompletnych rekordów w kolejce'}
          </p>
        </Card>
      </div>

      <div className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-3">
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <button
            type="button"
            onClick={() => setView('catalog')}
            className={`inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-semibold transition ${
              view === 'catalog'
                ? 'bg-ratuj-300 text-primary-dark-100'
                : 'bg-primary-dark-100 text-primary-light-300 hover:text-primary-light-100'
            }`}
          >
            Katalog ({catalogStats.total})
          </button>
          <button
            type="button"
            onClick={() => setView('moderation')}
            className={`inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-semibold transition ${
              view === 'moderation'
                ? 'bg-ratuj-300 text-primary-dark-100'
                : 'bg-primary-dark-100 text-primary-light-300 hover:text-primary-light-100'
            }`}
          >
            Moderacja ({suggestionStats.total})
          </button>
        </div>
      </div>

      {view === 'catalog' ? (
        <div className="space-y-4">
          <Card className="p-4 sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-primary-light-100">Dodaj nowy typ</h2>
                <p className="mt-1 text-sm text-primary-light-300">
                  Admin tworzy canonical rekord z nazwą i stabilnym shortCode.
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-primary-light-400">
                <MagnifyingGlassIcon className="h-4 w-4" />
                {catalogStats.active} aktywnych pozycji w read modelu
              </div>
            </div>

            <form className="mt-4 space-y-3" onSubmit={handleCreateEquipmentType}>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="space-y-1">
                  <span className="text-sm font-medium text-primary-light-300">Nazwa</span>
                  <input
                    value={creatingDraft.name}
                    onChange={(event) =>
                      setCreatingDraft((current) => ({ ...current, name: event.target.value }))
                    }
                    placeholder="np. Laptopy"
                    className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:border-ratuj-300 focus:outline-none"
                    required
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-sm font-medium text-primary-light-300">shortCode</span>
                  <input
                    value={creatingDraft.shortCode}
                    onChange={(event) =>
                      setCreatingDraft((current) => ({ ...current, shortCode: event.target.value }))
                    }
                    placeholder="np. LAP"
                    className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:border-ratuj-300 focus:outline-none"
                    required
                  />
                </label>
              </div>

              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <p className="text-xs text-primary-light-400">
                  shortCode zostanie znormalizowany po stronie backendu i pozostanie stabilny.
                </p>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={busyKey === 'catalog-create'}
                  icon={<PlusIcon className="h-4 w-4" />}
                >
                  Dodaj typ
                </Button>
              </div>
            </form>
          </Card>

          <Card className="p-4 sm:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-primary-light-100">Katalog</h2>
                <p className="text-sm text-primary-light-300">
                  Aktywne i nieaktywne rekordy canonical. Żadnych lokalnych list w UI.
                </p>
              </div>
              <label className="w-full max-w-sm space-y-1">
                <span className="text-sm font-medium text-primary-light-300">Szukaj</span>
                <input
                  value={catalogSearch}
                  onChange={(event) => setCatalogSearch(event.target.value)}
                  placeholder="Nazwa, shortCode, status..."
                  className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:border-ratuj-300 focus:outline-none"
                />
              </label>
            </div>

            {loadingCatalog && catalog.length === 0 ? (
              <div className="py-8">
                <PageLoadingState
                  title="Ładowanie katalogu"
                  message="Pobieramy canonical rekordy typów sprzętu."
                />
              </div>
            ) : filteredCatalog.length === 0 ? (
              <div className="py-4">
                <PageEmptyState
                  title="Brak typów w katalogu"
                  message="Dodaj pierwszy canonical typ sprzętu, aby otworzyć read model dla consumerów."
                />
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
                {filteredCatalog.map((item) => {
                  const isEditing = editingEquipmentTypeId === item.equipmentTypeId;
                  const isBusy =
                    busyKey === `catalog-edit-${item.equipmentTypeId}` ||
                    busyKey === `catalog-toggle-${item.equipmentTypeId}`;

                  return (
                    <article
                      key={item.equipmentTypeId}
                      className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-100/55 p-4"
                    >
                      {isEditing ? (
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="info" size="sm">
                              Edycja
                            </Badge>
                            {getStatusBadge(item.status)}
                          </div>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <label className="space-y-1">
                              <span className="text-sm font-medium text-primary-light-300">
                                Nazwa
                              </span>
                              <input
                                value={editingDraft.name}
                                onChange={(event) =>
                                  setEditingDraft((current) => ({
                                    ...current,
                                    name: event.target.value,
                                  }))
                                }
                                className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 py-2 text-sm text-primary-light-100 focus:border-ratuj-300 focus:outline-none"
                              />
                            </label>
                            <label className="space-y-1">
                              <span className="text-sm font-medium text-primary-light-300">
                                shortCode
                              </span>
                              <input
                                value={editingDraft.shortCode}
                                onChange={(event) =>
                                  setEditingDraft((current) => ({
                                    ...current,
                                    shortCode: event.target.value,
                                  }))
                                }
                                className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 py-2 text-sm text-primary-light-100 focus:border-ratuj-300 focus:outline-none"
                              />
                            </label>
                          </div>
                          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                            <Button
                              type="button"
                              variant="neutral"
                              size="sm"
                              onClick={cancelEdit}
                              disabled={isBusy}
                            >
                              Anuluj
                            </Button>
                            <Button
                              type="button"
                              variant="primary"
                              size="sm"
                              onClick={() => {
                                void handleSaveEdit();
                              }}
                              disabled={isBusy}
                              icon={<PencilSquareIcon className="h-4 w-4" />}
                            >
                              Zapisz
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-base font-semibold text-primary-light-100">
                                  {item.name}
                                </h3>
                                {getStatusBadge(item.status)}
                              </div>
                              <p className="mt-1 text-sm text-primary-light-300">
                                {item.shortCode}
                              </p>
                              <p className="mt-1 text-xs text-primary-light-500">
                                Źródło: {item.source === 'suggestion' ? 'sugestia admina' : 'admin'}
                              </p>
                            </div>
                            <Badge variant="neutral" size="sm">
                              {item.shortCode}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-1 gap-2 text-xs text-primary-light-400 sm:grid-cols-2">
                            <p>Utworzono: {formatTimestamp(item.createdAt)}</p>
                            <p>Aktualizacja: {formatTimestamp(item.updatedAt)}</p>
                            <p>Aktywacja: {formatTimestamp(item.activatedAt)}</p>
                            <p>Dezaktywacja: {formatTimestamp(item.deactivatedAt)}</p>
                          </div>

                          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                            <Button
                              type="button"
                              variant="neutral"
                              size="sm"
                              onClick={() => startEdit(item)}
                              disabled={busyKey !== null}
                              icon={<PencilSquareIcon className="h-4 w-4" />}
                            >
                              Edytuj
                            </Button>
                            <Button
                              type="button"
                              variant={item.status === 'active' ? 'error' : 'success'}
                              size="sm"
                              onClick={() => {
                                void handleToggleActiveState(item);
                              }}
                              disabled={isBusy}
                              icon={
                                item.status === 'active' ? (
                                  <NoSymbolIcon className="h-4 w-4" />
                                ) : (
                                  <CheckCircleIcon className="h-4 w-4" />
                                )
                              }
                            >
                              {item.status === 'active' ? 'Wycofaj' : 'Przywróć'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      ) : (
        <div className="space-y-4">
          <Card className="p-4 sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-primary-light-100">Kolejka moderacji</h2>
                <p className="mt-1 text-sm text-primary-light-300">
                  Admin akceptuje lub odrzuca sugestie serviceAdmin. Po akceptacji powstaje
                  canonical rekord.
                </p>
              </div>
              <div className="text-sm text-primary-light-400">
                {suggestionStats.total} sugestii oczekujących
              </div>
            </div>

            {loadingSuggestions && pendingSuggestions.length === 0 ? (
              <div className="py-8">
                <PageLoadingState
                  title="Ładowanie moderacji"
                  message="Pobieramy sugestie oczekujące na decyzję."
                />
              </div>
            ) : pendingSuggestions.length === 0 ? (
              <div className="py-4">
                <PageEmptyState
                  title="Brak sugestii do moderacji"
                  message="Nowe propozycje od serviceAdmin pojawią się tutaj po wysłaniu do backendu."
                />
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
                {pendingSuggestions.map((suggestion) => {
                  const draft = ensureSuggestionDraft(suggestion.equipmentTypeSuggestionId);
                  const isIncomplete = !suggestion.suggestedName.trim();
                  const approveBusy =
                    busyKey === `suggestion-approve-${suggestion.equipmentTypeSuggestionId}`;
                  const rejectBusy =
                    busyKey === `suggestion-reject-${suggestion.equipmentTypeSuggestionId}`;

                  return (
                    <article
                      key={suggestion.equipmentTypeSuggestionId}
                      className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-100/55 p-4"
                    >
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="info" size="sm">
                            Pending
                          </Badge>
                          {isIncomplete ? (
                            <Badge variant="warning" size="sm">
                              Niekompletna
                            </Badge>
                          ) : null}
                        </div>

                        <div>
                          <h3 className="text-base font-semibold text-primary-light-100">
                            {suggestion.suggestedName || 'Brak nazwy w rekordzie'}
                          </h3>
                          <p className="mt-1 text-sm text-primary-light-300">
                            Zgłosił: {suggestion.suggestedByDisplayName || suggestion.suggestedBy}
                          </p>
                          <p className="text-xs text-primary-light-500">
                            Rola: {suggestion.suggestedByRole || 'Brak danych'} ·{' '}
                            {formatTimestamp(suggestion.createdAt)}
                          </p>
                        </div>

                        {suggestion.notes && (
                          <div className="rounded-xl border border-primary-dark-300/60 bg-primary-dark-200/50 p-3 text-sm text-primary-light-300">
                            {suggestion.notes}
                          </div>
                        )}

                        <label className="space-y-1">
                          <span className="text-sm font-medium text-primary-light-300">
                            shortCode po akceptacji
                          </span>
                          <input
                            value={draft.shortCode}
                            onChange={(event) =>
                              updateSuggestionDraft(suggestion.equipmentTypeSuggestionId, {
                                shortCode: event.target.value,
                              })
                            }
                            placeholder="np. LAP"
                            className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:border-ratuj-300 focus:outline-none"
                            disabled={isIncomplete}
                          />
                        </label>

                        <label className="space-y-1">
                          <span className="text-sm font-medium text-primary-light-300">
                            Powód odrzucenia
                          </span>
                          <textarea
                            value={draft.rejectionReason}
                            onChange={(event) =>
                              updateSuggestionDraft(suggestion.equipmentTypeSuggestionId, {
                                rejectionReason: event.target.value,
                              })
                            }
                            rows={2}
                            placeholder="Opcjonalny komentarz dla serviceAdmin"
                            className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:border-ratuj-300 focus:outline-none"
                          />
                        </label>

                        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                          <Button
                            type="button"
                            variant="neutral"
                            size="sm"
                            onClick={() => {
                              void handleRejectSuggestion(suggestion);
                            }}
                            disabled={rejectBusy}
                            icon={<XCircleIcon className="h-4 w-4" />}
                          >
                            Odrzuć
                          </Button>
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              void handleApproveSuggestion(suggestion);
                            }}
                            disabled={approveBusy || isIncomplete}
                            icon={<CheckCircleIcon className="h-4 w-4" />}
                          >
                            Akceptuj
                          </Button>
                        </div>

                        {isIncomplete && (
                          <p className="text-xs text-status-warning-500">
                            Ten rekord jest niekompletny. Można go odrzucić, ale nie zaakceptować
                            bez nazwy.
                          </p>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default EquipmentTypeManagementPage;
